import {FC, useEffect, useMemo} from 'react'
import {UserEditingFieldsGroup, useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {useMutationState} from '@tanstack/react-query'
import {MUTATION_KEYS} from '@/queryClient'
import {HttpUpdateUserOptions} from '@/features/users/http/users.http'
import {ObjectKeys} from '@/utilities/helpers'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {usePermissions} from '@/features/auth/store/store'

interface UserDetailsEditButtonsProps {
    groupName: UserEditingFieldsGroup
    isConfirmDisabled: boolean
    onCancel: () => void
    onConfirm: () => void
}

export const UserDetailsEditButtons: FC<UserDetailsEditButtonsProps> = ({
    groupName,
    isConfirmDisabled,
    onCancel,
    onConfirm
}) => {
    const permissions = usePermissions()
    const currentEditingFieldsGroup = useUserDetailsStore(state => state.editingFieldsGroup)
    const setEditingFieldsGroup = useUserDetailsStore(state => state.setEditingFieldsGroup)

    // retrieve pending state from mutation
    const updateUserState = useMutationState({
        filters: {mutationKey: [MUTATION_KEYS.UPDATE_USER], status: 'pending'},
        select: mutation => ({
            variables: mutation.state.variables as HttpUpdateUserOptions
        })
    })

    // constants
    const isLoadingUpdate = useMemo(() => !!ObjectKeys(updateUserState).length, [updateUserState])
    const isActive = useMemo(() => groupName == currentEditingFieldsGroup, [groupName, currentEditingFieldsGroup])

    // clear store on unmount
    useEffect(() => {
        return () => setEditingFieldsGroup(null)
    }, [])

    return (
        <EditDetailsSectionButtons
            isActive={isActive}
            isLoading={isLoadingUpdate}
            isDisabled={isConfirmDisabled && isActive}
            onEdit={permissions?.users?.write ? () => setEditingFieldsGroup(groupName) : undefined}
            onConfirm={onConfirm}
            onCancel={() => {
                setEditingFieldsGroup(null)
                onCancel()
            }}
        />
    )
}
